import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ServiceSchedule, ScheduleItem } from '../serviceLog';

interface ServiceScheduleViewProps {
    schedule: ServiceSchedule;
    vehicleModel: string;
}

const ServiceScheduleView: React.FC<ServiceScheduleViewProps> = ({
    schedule,
    vehicleModel,
}) => {
    const serviceNumbers = Object.keys(schedule).sort((a, b) => Number(a) - Number(b));

    // Helper function to get items safely and get categories
    const getItemsForCategory = (serviceData: any, category: string) => {
        return (serviceData[category] || []) as ScheduleItem[];
    };

    const getCategoriesFromServiceData = (serviceData: any) => {
        return Object.keys(serviceData).filter(key =>
            Array.isArray(serviceData[key]) &&
            !['dueOnKms', 'intervalKms', 'dueOnPeriod', 'intervalPeriod'].includes(key)
        );
    };

    return (
        <Box>
            {serviceNumbers.map((serviceNumber, index) => {
                const serviceData = schedule[serviceNumber];
                if (!serviceData) return null;
                const { dueOnKms, intervalKms, dueOnPeriod, intervalPeriod } = serviceData;
                const categories = getCategoriesFromServiceData(serviceData);

                return (
                    <Accordion key={serviceNumber} sx={{ mb: 1 }} defaultExpanded={true}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                                        Service #{index + 1}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                            - {dueOnKms.toLocaleString()} km / {dueOnPeriod} months
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {categories.map(category => {
                                    const items = getItemsForCategory(serviceData, category);
                                    if (items.length === 0) return null;

                                    return (
                                        <Grid item xs={12} md={6} key={category}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    height: '100%',
                                                    backgroundColor: 'rgba(163, 163, 163, 0.02)'
                                                }}
                                            >
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                                        {category}
                                                    </Typography>
                                                </Box>
                                                <List dense disablePadding>
                                                    {items.map((item, idx) => (
                                                        <React.Fragment key={item.id}>
                                                            {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                                                            <ListItem disableGutters sx={{ py: 0.5 }}>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                                            {item.item} - {item.action}
                                                                        </Typography>
                                                                    }
                                                                    secondary={
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {item.remarks}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        </React.Fragment>
                                                    ))}
                                                </List>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                                {!categories.some(category => getItemsForCategory(serviceData, category).length > 0) && (
                                    <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No service items scheduled
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

const getCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (category.toUpperCase()) {
        case 'LUBRICATION':
            return 'primary';
        case 'INSPECTION':
            return 'info';
        case 'MAINTENANCE':
            return 'success';
        case 'SAFETY':
            return 'error';
        case 'PERIODIC':
            return 'secondary';
        case 'OTHER':
            return 'warning';
        default:
            return 'default';
    }
};

export default ServiceScheduleView; 